import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { LoadingSpinnerComponent } from '../../../../../core/shared/components/loading-spinner/loading-spinner.component';
import { ApplicationModalComponent } from '../../../../../core/shared/components/modals/application-modal/application-modal.component';
import { GetApplications } from '../../../../../core/shared/actions/application.actions';
import { Application } from '../../../../../core/shared/models/application.model';
import { ApplicationState } from '../../../../../core/shared/states/application.state';

@Component({
  selector: 'app-admin-applications-list',
  standalone: true,
  templateUrl: './admin-applications-list.component.html',
  styleUrl: './admin-applications-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    LoadingSpinnerComponent,
    MatInputModule,
    MatIcon,
    MatIconButton,
  ],
})
export class AdminApplicationsListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  public paginator!: MatPaginator;
  public sort!: MatSort;
  public dataSource = new MatTableDataSource<Application>();
  public displayedColumns = ['icon', 'application_name', 'Description', 'approved', 'actions'];
  public loading = true;
  public applicationsCount = 0;
  public activeApplications = 0;
  public awaitingApplications = 0;
  public readonly applications$: Observable<Application[]>;

  constructor(
    private readonly store: Store,
    private readonly modalService: NgbModal,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.store.dispatch(new GetApplications());
    this.applications$ = this.store.select(ApplicationState.getApplicationList);
  }

  ngOnInit() {
    this.applications$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.applicationsCount = data.length;
        this.activeApplications = data.filter((application) => application.approved).length;
        this.awaitingApplications = this.applicationsCount - this.activeApplications;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  openModal(application: Application, formMode: string, editApplication: boolean, admin: boolean) {
    const modalRef = this.modalService.open(ApplicationModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.application = application;
    modalRef.componentInstance.formMode = formMode;
    modalRef.componentInstance.editApplication = editApplication;
    modalRef.componentInstance.admin = admin;
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }
}
